/**
 * The single door to the API.
 *
 * Every call goes through `request`, so authentication refresh, error shaping
 * and credential handling exist in exactly one place. Components never call
 * fetch directly.
 */
import type { ApiError } from '@genoma/shared';

const BASE_URL = '/api';

/**
 * The language the interface is currently in, sent with every request.
 *
 * Behaviour names and anchors are stored in both languages and rendered by the
 * API, so switching the interface has to reach the server or a report stays in
 * Spanish under English headings — which is exactly what happened. Read from
 * the same key the i18n provider writes, rather than passed down through every
 * call site, so a new endpoint gets it without anybody remembering to.
 */
const currentLocale = (): string => {
  // Guarded because this module is exercised under Node, where there is no
  // storage — and an unguarded read threw before the request was even made,
  // which took out every test that loads a survey rather than showing up as
  // a language bug.
  try {
    return globalThis.localStorage?.getItem('genoma.locale') ?? 'es';
  } catch {
    return 'es';
  }
};

/**
 * The cycle the panel is looking at, when somebody picked one in the rail.
 *
 * Null means "whatever the server defaults to", which is what every screen did
 * before there was more than one cycle. Added here, like the language, so the
 * dozen panel calls follow the selector without each being given a parameter
 * — and so a new panel endpoint does too.
 */
let cycleScope: string | null = null;

export const setCycleScope = (cycleId: string | null): void => {
  cycleScope = cycleId;
};

/** Panel endpoints answer about a cycle; everything else ignores the scope. */
const CYCLE_SCOPED = /^\/(dashboard|coaches)(\/|\?|$)/;

const withCycleScope = (path: string): string => {
  if (!cycleScope || !CYCLE_SCOPED.test(path) || path.startsWith('/dashboard/cycles')) {
    return path;
  }
  if (/[?&]cycleId=/.test(path)) return path;
  return `${path}${path.includes('?') ? '&' : '?'}cycleId=${encodeURIComponent(cycleScope)}`;
};

export class ApiRequestError extends Error {
  // Written out rather than declared as constructor parameter properties, so
  // the file runs under Node's type stripping and the store can be tested
  // without a build step.
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, string[]> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Internal: set when replaying a call after a token refresh. */
  retried?: boolean;
};

/**
 * One refresh in flight at a time. Without this, a page that fires several
 * requests on mount would trigger a refresh per request and rotate the token
 * out from under itself.
 */
let refreshInFlight: Promise<boolean> | null = null;

const refreshSession = async (): Promise<boolean> => {
  refreshInFlight ??= fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

export const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { method = 'GET', body, signal, retried = false } = options;

  const response = await fetch(`${BASE_URL}${withCycleScope(path)}`, {
    method,
    credentials: 'include',
    headers: {
      'Accept-Language': currentLocale(),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  // An expired access token is recoverable exactly once per call.
  if (response.status === 401 && !retried && path !== '/auth/refresh') {
    if (await refreshSession()) {
      return request<T>(path, { ...options, retried: true });
    }
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const apiError = (payload as ApiError | null)?.error;
    throw new ApiRequestError(
      response.status,
      apiError?.code ?? 'UNKNOWN',
      apiError?.message ?? 'The request could not be completed.',
      apiError?.details,
    );
  }

  return payload as T;
};

export const get = <T>(path: string, signal?: AbortSignal) =>
  request<T>(path, { method: 'GET', ...(signal ? { signal } : {}) });

export const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body });

export const patch = <T>(path: string, body?: unknown, signal?: AbortSignal) =>
  request<T>(path, { method: 'PATCH', body, ...(signal ? { signal } : {}) });

export const put = <T>(path: string, body?: unknown, signal?: AbortSignal) =>
  request<T>(path, { method: 'PUT', body, ...(signal ? { signal } : {}) });

export const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });
